/**
 * Sin conexión (006 · T019, research R-20; contracts/staff-ui.md §8).
 *
 * - `OfflineState` (`offline-state`): cuando una pantalla de datos (Inicio, Asistencia, Perfil)
 *   no tiene nada cargado y no hay red. Icono + "Sin conexión" + "Revisa tu conexión a internet e
 *   inténtalo de nuevo." + botón secundario "Reintentar". Va **dentro** de la pantalla (con su
 *   barra de pestañas), no la reemplaza.
 * - `OfflineBanner` (`offline-banner`, `role="status"`): aviso fijo cuando se pierde la red con
 *   datos ya cargados en esta sesión: "Sin conexión. Mostramos lo último que cargaste."
 *
 * Estado siempre con icono **y** texto (§10). Textos de `i18n/es-PE/shell.json`.
 */
import { WifiOff } from 'lucide-react';

import { t } from '../../i18n';

import { Button } from './button';
import { cn } from './cn';

export interface OfflineStateProps {
  onRetry: () => void;
  /** Deshabilita "Reintentar" mientras se reintenta. */
  retrying?: boolean;
  className?: string;
}

export function OfflineState({ onRetry, retrying = false, className }: OfflineStateProps) {
  return (
    <section
      data-testid="offline-state"
      aria-labelledby="offline-state-title"
      className={cn('flex flex-col items-center gap-fm-3 py-fm-8 text-center', className)}
    >
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-fm-pill bg-fm-bg-sunken text-fm-text-secondary"
      >
        <WifiOff size={28} />
      </span>
      <h2 id="offline-state-title" className="font-display text-fm-lg font-bold text-fm-text">
        {t('shell.offline.title')}
      </h2>
      <p className="text-fm-base text-fm-text-secondary">{t('shell.offline.description')}</p>
      <Button
        variant="secondary"
        onClick={onRetry}
        loading={retrying}
        loadingText={t('common.states.loading')}
        className="mt-fm-2"
      >
        {t('shell.offline.retry')}
      </Button>
    </section>
  );
}

export function OfflineBanner({ className }: { className?: string }) {
  return (
    <p
      data-testid="offline-banner"
      role="status"
      className={cn(
        'flex items-center gap-fm-2 rounded-fm-md bg-fm-bg-sunken p-fm-3 text-fm-sm text-fm-text',
        className,
      )}
    >
      <WifiOff aria-hidden="true" size={18} className="shrink-0 text-fm-text-secondary" />
      <span>{t('shell.offline.banner')}</span>
    </p>
  );
}
